import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthorizationGuardService } from '../../shared/libs/auth/authorization-guard.service';
import { CompanyComponent } from './components/company.component';
import { CompanyDashboardComponent } from './components/company-dashboard/company-dashboard.component';
import { CompanyOrderListComponent } from './components/company-order-list/company-order-list.component';
import { CompanyStyleDetailComponent } from './components/company-style-detail/company-style-detail.component';
import { CompanyStyleListComponent } from './components/company-style-list/company-style-list.component';
import { CompanyOrderDetailComponent } from './components/company-order-detail/company-order-detail.component';
import { CompanyOrderDetailInfoComponent } from './components/company-order-detail/company-order-detail-info/company-order-detail-info.component';
import { CompanyOrderDetailShotsComponent } from './components/company-order-detail/company-order-detail-shots/company-order-detail-shots.component';
import { CompanyTemplateListComponent } from './components/company-template-list/company-template-list.component';
import { CompanyExportTemplateListComponent } from './components/company-third-parties-applications/company-export-template-list/company-export-template-list.component';
import { CompanyTemplateDetailComponent } from './components/company-template-detail/company-template-detail.component';
import { CompanyExportTemplateDetailComponent } from './components/company-third-parties-applications/company-export-template-detail/company-export-template-detail.component';
import { CompanyThirdPartiesApplicationsComponent } from './components/company-third-parties-applications/company-third-parties-applications.component';
import { CompanyLegacyImageFormatsComponent } from './components/company-third-parties-applications/company-legacy-image-formats/company-legacy-image-formats.component';
import { CompanyPhotographerDetailComponent } from './components/company-photographer-detail/company-photographer-detail.component';
import { CompanyPhotographerListComponent } from './components/company-photographer-list/company-photographer-list.component';
import { MediaVisualizerComponent } from './components/media-visualizer/media-visualizer.component';
import { Roles } from 'src/app/shared/types/role.enum';
import { CompanyRoleSettingsComponent } from './components/company-role-settings/company-role-settings.component';
import { CompanyInsightStylePriceComponent } from './components/company-insight-style-price/company-insight-style-price.component';
import { CompanyMediaShareLinkListComponent } from './components/company-media-share-link-list/company-media-share-link-list.component';
import { CompanyMediaShareLinkDetailComponent } from './components/company-media-share-link-detail/company-media-share-link-detail.component';
import { CompanyShotTypesListComponent } from './components/company-shot-types-list/company-shot-types-list.component';

const routes: Routes = [
  {
    path: '',
    component: CompanyComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'dashboard',
        component: CompanyDashboardComponent,
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'order-list',
        component: CompanyOrderListComponent,
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        canActivate: [AuthorizationGuardService]
      },
    
      {
        path: 'order-info/order-detail-info/:orderId',
        redirectTo: 'order-view/:orderId/order-detail-info',
        pathMatch: 'full',
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] }
      },
      {
        path: 'order-info/order-detail-shots/:orderId',
        redirectTo: 'order-view/:orderId/order-detail-shots',
        pathMatch: 'full',
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] }
      },
    
      {
        path: 'order-view',
        redirectTo: 'order-view/new',
        pathMatch: 'full',
        data: { roles: [Roles.COMPANY_ADMIN] }
      },
      {
        path: 'order-view/:orderId',
        component: CompanyOrderDetailComponent,
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        canActivate: [AuthorizationGuardService],
        children: [
          {
            path: '',
            redirectTo: 'order-detail-info',
            pathMatch: 'full'
          },
          {
            path: 'order-detail-info',
            component: CompanyOrderDetailInfoComponent
          },
          {
            path: 'order-detail-shots',
            component: CompanyOrderDetailShotsComponent
          }
        ]
      },
    
      {
        path: 'style-list',
        component: CompanyStyleListComponent,
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'style-info/:modelId',
        component: CompanyStyleDetailComponent,
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'photographer-list',
        component: CompanyPhotographerListComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'photographer-detail/:photographerId',
        component: CompanyPhotographerDetailComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
    
      {
        path: 'template-list',
        component: CompanyTemplateListComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'template-detail/:templateId',
        component: CompanyTemplateDetailComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'export-pim-template-list',
        redirectTo: 'third-parties-applications',
        pathMatch: 'full'
      },
      {
        path: 'third-parties-applications',
        component: CompanyThirdPartiesApplicationsComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        // Before ':channelId', which would otherwise take "legacy-image-formats" as a channel id.
        path: 'third-parties-applications/legacy-image-formats',
        component: CompanyLegacyImageFormatsComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'third-parties-applications/:channelId',
        component: CompanyExportTemplateListComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'third-parties-applications/:channelId/:exportTemplateId',
        component: CompanyExportTemplateDetailComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'export-pim-template-detail/:exportTemplateId',
        redirectTo: 'third-parties-applications',
        pathMatch: 'full'
      },
    
      /* sezione completamente commentata dopo call con Fabio Franzin 31 luglio 2026
      {
        path: 'cost-templates-management-list',
        component: CompanyCostTemplatesManagementListComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      */
    
      {
        path: 'user-role-settings',
        component: CompanyRoleSettingsComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'media-visualizer',
        component: MediaVisualizerComponent
        // data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        // canActivate: [AuthorizationGuardService]
      },
      {
        path: 'insight-style-price',
        component: CompanyInsightStylePriceComponent,
        data: { roles: [Roles.COMPANY_ADMIN, Roles.COMPANY_USER] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'media-share-link-list',
        component: CompanyMediaShareLinkListComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'media-share-link-detail/:mediaShareLinkId',
        component: CompanyMediaShareLinkDetailComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      },
      {
        path: 'shot-types-list',
        component: CompanyShotTypesListComponent,
        data: { roles: [Roles.COMPANY_ADMIN] },
        canActivate: [AuthorizationGuardService]
      }
		]
	}
];

@NgModule({
  imports: [RouterModule.forChild(routes)]
})
export class CompanyRouting {}
